// bounded-login-widget.ts - the UNIFIED Bounded login surface: one in-app card
// (Shadow-DOM modal from bounded-widget-modal.ts, Bounded look + motion) that folds
// together every login lane the SDK supports:
//   - Email / social (Google, ...) -> the hosted Better Auth OIDC flow, run in a
//     brief FIRST-PARTY popup (loginWithPopup). A cross-origin auth IFRAME cannot
//     hold the Better Auth session cookie (third-party-cookie blocking in Safari /
//     Chrome), so the session handshake pops; the card itself stays in-app.
//   - "Continue with wallet" -> native injected wallets (Phantom / Solflare /
//     Backpack) via loginWithWallet(), run ENTIRELY on the app page (injected
//     providers only exist there, never inside a cross-origin frame).
//
// Redirect stays available as the popup-blocked fallback (loginWithRedirect).
// The same modal is reused for Turnkey wallet signing, so login -> first
// signature is one continuous surface.
import { getModal, showModal, hideModal, mountContent, fitPanelToContent, ensureFonts, hasDOM, } from "./bounded-widget-modal";
import { claimModalOpen } from "./bounded-widget-ownership";
import { discoverStandardWallets } from "./providers/wallet-standard-discovery";
import { looksLikeSolanaMobileWallet, WalletConfigError } from "./providers/solana-mobile-registration";
import { isWalletLaneEnabled, shouldOfferSolanaMobileWallet } from "./wallet-lane";
import { startTurnkeyEmailLogin } from "./turnkey-auth";
// Palette + type ladder mirror the hosted auth page (login-page.ts): dark editorial
// panel, Newsreader display serif for the title, Schibsted Grotesk body, a light
// (paper) primary button and inset inputs. This is the same look/feel as
// auth.bounded, brought inline into the in-app widget.
const CARD_CSS = `
:host,*{box-sizing:border-box}
.c{--bg-inset:#060607;--bg-raise:#131416;
  --line:rgba(232,230,222,.08);--line-strong:rgba(232,230,222,.16);
  --text:#e8e6de;--dim:#9b9991;--faint:#85837c;
  --green:#58c98f;--green-bg:rgba(88,201,143,.09);--green-line:rgba(88,201,143,.28);--red:#ec8488;
  --disp:"Newsreader","Iowan Old Style",Georgia,serif;
  --body:"Schibsted Grotesk","Helvetica Neue",Arial,sans-serif;
  position:relative;padding:34px 32px 20px;color:var(--text);font-family:var(--body);
  -webkit-font-smoothing:antialiased}
.x{position:absolute;top:20px;right:24px;border:0;background:transparent;color:var(--faint);
  font-family:var(--body);font-size:13px;line-height:1;cursor:pointer;padding:4px}
.x:hover{color:var(--text)}
h1{font-family:var(--disp);font-weight:400;font-size:27px;line-height:1.12;letter-spacing:-.01em;margin:0 0 6px;max-width:88%}
.sub{margin:0 0 24px;color:var(--dim);font-size:13.5px}
label{display:block;font-size:12px;color:var(--dim);margin:0 0 7px}
input{width:100%;padding:11px 13px;border-radius:6px;border:1px solid var(--line-strong);
  background:var(--bg-inset);color:var(--text);font-size:15px;font-family:var(--body);outline:none;
  transition:border-color .15s,box-shadow .15s}
input::placeholder{color:var(--faint)}
input:focus{border-color:var(--green-line);box-shadow:0 0 0 3px var(--green-bg)}
input.otp{font-family:var(--mono,ui-monospace,monospace);letter-spacing:8px;text-align:center;font-size:21px;padding-left:8px}
.btn{width:100%;padding:12px 14px;border-radius:6px;font-size:14px;font-weight:600;
  cursor:pointer;font-family:var(--body);display:flex;align-items:center;justify-content:center;gap:9px;
  transition:opacity .15s ease,background .15s ease,border-color .15s ease,transform .04s}
.btn:disabled{opacity:.45;cursor:default}
.primary{border:0;background:var(--text);color:#0a0a0b;margin-top:16px}
.primary:hover:not(:disabled){opacity:.9}
.primary:active:not(:disabled){transform:translateY(.5px)}
.ghost{border:1px solid var(--line-strong);background:var(--bg-raise);color:var(--text);margin-top:9px}
.ghost:hover:not(:disabled){background:#17181b}
.ghost svg{width:16px;height:16px;flex:none}
.wicon{width:18px;height:18px;border-radius:4px;flex:none}
.or{display:flex;align-items:center;gap:12px;margin:20px 0 14px;color:var(--faint);
  font-size:11px;letter-spacing:.08em;text-transform:uppercase}
.or::before,.or::after{content:"";flex:1;height:1px;background:var(--line)}
.status{margin-top:16px;min-height:16px;font-size:12.5px;color:var(--dim)}
.status.err{color:var(--red)}
.foot{margin-top:24px;padding-top:15px;border-top:1px solid var(--line);
  color:var(--faint);font-size:11px;text-align:center;letter-spacing:.02em}
.foot a{color:var(--dim);text-decoration:none}
.foot a:hover{color:var(--text)}
.back{background:transparent;border:0;color:var(--dim);font-size:12.5px;cursor:pointer;padding:0;margin-bottom:16px}
.back:hover{color:var(--text)}
.spin{width:15px;height:15px;border-radius:50%;border:2px solid rgba(10,10,11,.3);
  border-top-color:#0a0a0b;animation:bwspin .7s linear infinite}
.ghost .spin,.primary.wallet-busy .spin{border:2px solid var(--line-strong);border-top-color:var(--text)}
@keyframes bwspin{to{transform:rotate(360deg)}}
`;
// Brand glyphs, built as SVG DOM (never innerHTML) so the buttons match the hosted page.
function googleGlyph() {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 48 48");
    svg.setAttribute("aria-hidden", "true");
    const paths = [
        ["#EA4335", "M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"],
        ["#4285F4", "M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"],
        ["#FBBC05", "M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"],
        ["#34A853", "M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"],
    ];
    for (const [fill, d] of paths) {
        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.setAttribute("fill", fill);
        path.setAttribute("d", d);
        svg.appendChild(path);
    }
    return svg;
}
function walletGlyph() {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");
    svg.setAttribute("aria-hidden", "true");
    const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    rect.setAttribute("x", "3");
    rect.setAttribute("y", "6");
    rect.setAttribute("width", "18");
    rect.setAttribute("height", "13");
    rect.setAttribute("rx", "2.5");
    rect.setAttribute("stroke", "currentColor");
    rect.setAttribute("stroke-width", "1.6");
    const dot = document.createElementNS("http://www.w3.org/2000/svg", "circle");
    dot.setAttribute("cx", "16.5");
    dot.setAttribute("cy", "12.5");
    dot.setAttribute("r", "1.4");
    dot.setAttribute("fill", "currentColor");
    svg.append(rect, dot);
    return svg;
}
function isInjectedProvider(v) {
    return (typeof v === "object" &&
        v !== null &&
        typeof v.signMessage === "function");
}
// Enumerate native wallets via the Solana Wallet Standard registry (NOT a
// hardcoded name list) - every wallet that registered itself shows up with its
// real name + icon - and ALSO an older wallet that never registers and only
// exposes `window.solana`.
//
// The legacy `window.solana` probe fills in for a wallet too old to register
// itself. The rule it has always encoded is "no BROWSER wallet registered", and
// the registry answered that faithfully until Solana Mobile joined it: MWA
// registers on every capable Android phone, installed browser wallet or not, so
// a bare "is the registry empty" test would hide exactly the wallet the user
// has. Judge emptiness by the registered wallets that are actually in-page
// ones. (Identity de-duplication is not available here: a registry adapter is
// never the injected object, so comparing them can only ever be false.)
function discoverWallets(configuredNetwork) {
    if (typeof window === "undefined")
        return [];
    // DS2-0383: pass the app's configured Solana network so the discovered
    // provider signs+sends on that network, not the wallet's first advertised chain.
    const standard = discoverStandardWallets(configuredNetwork);
    const choices = standard.map((w) => ({ name: w.name, icon: w.icon, wallet: w.wallet, get: () => w.getProvider() }));
    // "Did an in-page wallet register?" is a question about TRANSPORT: a mobile
    // adapter another SDK registered is still not the injected wallet, so counting
    // it would hide a legacy window.solana wallet the user actually has.
    const registeredInPageWallet = standard.some((w) => !looksLikeSolanaMobileWallet(w.name));
    const generic = window.solana;
    if (!registeredInPageWallet && isInjectedProvider(generic)) {
        choices.push({ name: "Wallet", get: () => generic });
    }
    return choices;
}
// Shown when a lane is refused because another login can still commit. Saying so
// beats a click that does nothing, which is what "one at a time" looks like from
// the outside.
const BUSY_MSG = "A sign-in is already in progress - finish or cancel it first.";
const METHOD_LABEL = {
    google: "Continue with Google",
    apple: "Continue with Apple",
    github: "Continue with GitHub",
};
function el(tag, cls, text) {
    const n = document.createElement(tag);
    if (cls)
        n.className = cls;
    if (text != null)
        n.textContent = text;
    return n;
}
// The session-mint lock, deliberately OUTSIDE any one open. What it protects -
// the SDK's single stored session and committed auth provider - is global, and a
// login cannot be cancelled once it is running: dismissing a card or superseding
// it with a second openBoundedWidget() leaves the login underneath still able to
// commit. A per-open flag would hand the next card a fresh unlocked boolean and
// let exactly the overlap it exists to prevent happen across opens.
let loginInFlight = false;
// The open that currently owns the card. A login cannot be cancelled once it is
// running, so one started on a card that is then superseded or dismissed can
// still commit a session - and the widget must not answer that by leaving a live
// login card in front of a signed-in app.
let liveOpen = null;
const pendingOpens = new Set();
/** Register an open before its first await, superseding older pending ones. */
function beginPendingOpen() {
    for (const older of pendingOpens)
        cancelPendingOpen(older);
    const pending = { committed: null, cancelled: false, wake: null };
    pendingOpens.add(pending);
    return pending;
}
function cancelPendingOpen(pending) {
    pending.cancelled = true;
    const wake = pending.wake;
    pending.wake = null;
    wake === null || wake === void 0 ? void 0 : wake();
}
/** The user closed the widget: every open still preparing is closed with it. */
function cancelPendingOpens() {
    for (const pending of pendingOpens)
        cancelPendingOpen(pending);
}
/** A login committed: hand it to opens that have no card to receive it. */
function deliverToPendingOpens(user) {
    for (const pending of pendingOpens) {
        pending.committed = user;
        const wake = pending.wake;
        pending.wake = null;
        wake === null || wake === void 0 ? void 0 : wake();
    }
}
/**
 * Await preparation, but stop the moment this open's answer is already known -
 * dismissed, superseded, or overtaken by a login that committed. Without this a
 * stalled chunk fetch holds the caller on a card that can never be shown, or on
 * a question the widget has already answered.
 */
function untilCancelled(work, pending) {
    if (pending.cancelled || pending.committed)
        return Promise.reject(new Error("cancelled"));
    return Promise.race([
        work.finally(() => { pending.wake = null; }),
        new Promise((_, reject) => {
            pending.wake = () => reject(new Error("cancelled"));
        }),
    ]);
}
/**
 * Open the unified Bounded login widget and resolve with the signed-in `User`.
 * Rejects with `Error("cancelled")` if the user dismisses it. `init()` must have
 * run first; call from a user gesture so social/wallet popups are allowed.
 */
export async function openBoundedWidget(opts = {}) {
    if (!hasDOM()) {
        throw new Error("openBoundedWidget is web-only - on React Native use loginWithRedirect()");
    }
    // Registered before the first await - see PendingOpen.
    const pending = beginPendingOpen();
    try {
        return await prepareAndOpen(opts, pending);
    }
    catch (err) {
        // Interrupted rather than failed: while this open was preparing, a login
        // committed, the widget was dismissed, or a newer open was requested. The
        // committed login is the most useful answer of the three - the app IS signed
        // in - so it wins whatever else also happened.
        if (pending.committed)
            return pending.committed;
        if (pending.cancelled)
            throw new Error("cancelled");
        throw err;
    }
    finally {
        pendingOpens.delete(pending);
    }
}
/**
 * Prepare the card and run it. Split out so the caller can hold ONE record for
 * this open across every await below, and settle it the moment the widget is
 * dismissed rather than when preparation happens to finish.
 */
async function prepareAndOpen(opts, pending) {
    var _a, _b, _c, _d, _e, _f;
    // Imported lazily to avoid a static cycle with the auth index barrel.
    const authApi = await untilCancelled(import("./index"), pending);
    // BEFORE any side effect. Registering the mobile wallet from an empty config
    // would take the chainless mainnet default, and that registration cannot be
    // withdrawn - so opening the widget before init() would pin the page to the
    // wrong cluster and make the app's real init() fail demanding a reload.
    const initCfg = authApi.getInitConfig();
    if (!initCfg) {
        throw new Error("openBoundedWidget() must be called after init() - the login lane is built from the " +
            "app's configuration, and the mobile wallet is registered for its Solana network.");
    }
    const methods = ((_a = opts.methods) !== null && _a !== void 0 ? _a : ["email", "google"]).map(String);
    const hasEmail = methods.includes("email") || methods.includes("text");
    const social = methods.filter((m) => m !== "email" && m !== "text");
    // Policy first: an app whose site requires an email on file, or that explicitly
    // set `walletLogin: false`, hides the wallet lane entirely - this OVERRIDES a
    // per-call `opts.wallet` and any detected injected wallet.
    const lane = {
        requested: opts.wallet,
        hardOff: authApi.isWalletLoginDisabled(initCfg) || authApi.isEmailRequired(initCfg),
        optedIn: authApi.isWalletLoginEnabled(initCfg),
    };
    // Register Solana Mobile's MWA wallet (Saga/Seeker) with the Wallet Standard
    // registry BEFORE enumerating it, so the device wallet is listed alongside
    // injected wallets. Gated on the app's own opt-in (see wallet-lane.ts: MWA
    // registers on any capable Android browser, installed wallet or not, so it
    // must never be what turns the lane on). No-op off-Android.
    const network = authApi.getConfiguredSolanaNetwork(initCfg);
    // 1. Detect what is already there. This reads the registry and window.solana
    //    and loads nothing, so it is safe to do before anything is prepared - and
    //    it is what decides whether the lane appears at all.
    const offerMobile = shouldOfferSolanaMobileWallet(lane);
    const detected = discoverWallets(network)
        .filter((w) => !looksLikeSolanaMobileWallet(w.name)).length;
    let walletEnabled = isWalletLaneEnabled(Object.assign(Object.assign({}, lane), { detected }));
    // 2. Prepare BEFORE the lane is rendered, never after the tap: loading the
    //    provider chunk (or registering the mobile wallet) from a click spends the
    //    activation the wallet handoff needs. Preparing costs nothing for an app
    //    whose lane is off, because we only get here when it is on.
    if (walletEnabled) {
        try {
            // A mobile-wallet failure needs no special handling here: it simply is not
            // in the registry, so step 3 does not list it.
            if (offerMobile)
                await untilCancelled(authApi.ensureWalletLoginReady(), pending);
            // The lane is on through detection or opt-in but the phone wallet is not
            // being offered, so only the shared half is needed.
            else
                await untilCancelled(authApi.ensureSharedWalletLoginReady(), pending);
        }
        catch (err) {
            // Except when the app's own configuration is the problem. That is a
            // developer bug the SDK deliberately raises loudly, and swallowing it here
            // would answer "my wallet lane vanished, and so did my injected wallet"
            // with silence. Availability is what may degrade; a contradiction is not.
            if (err instanceof WalletConfigError)
                throw err;
            // Nor is being closed while preparing: this open is over, and continuing
            // to build its card would only render it after the user said no.
            if (pending.cancelled)
                throw err;
            // A SHARED failure means there is no wallet login at all: better no lane
            // than a button that fails after the user has spent a tap on it.
            walletEnabled = false;
        }
    }
    // 3. Enumerate AFTER preparing, so the list reflects what is actually ready -
    //    the mobile wallet exists in the registry only once registered.
    const wallets = walletEnabled ? discoverWallets(network) : [];
    // Nothing to offer: the phone wallet could not be prepared (or this device has
    // none) and the user has no injected wallet either. Hiding the lane beats a
    // button with no wallet behind it.
    if (wallets.length === 0)
        walletEnabled = false;
    const authMode = (_b = opts.authMode) !== null && _b !== void 0 ? _b : authApi.resolveAuthMode(initCfg);
    // Widget text: per-call options win, then init() loginWidget config, then the
    // defaults ("Sign in" / the Bounded subline; "" suppresses the subline).
    const title = (_c = opts.title) !== null && _c !== void 0 ? _c : (_d = initCfg.loginWidget) === null || _d === void 0 ? void 0 : _d.title;
    const subtitle = (_e = opts.subtitle) !== null && _e !== void 0 ? _e : (_f = initCfg.loginWidget) === null || _f === void 0 ? void 0 : _f.subtitle;
    ensureFonts();
    const m = getModal();
    // Whatever happened while this open was preparing, decided now, before it can
    // put anything on screen. A committed login wins over a cancellation: the app
    // IS signed in, and that is the more useful answer to the caller's question.
    if (pending.committed)
        return pending.committed;
    if (pending.cancelled)
        throw new Error("cancelled");
    // From here this open OWNS a card, and the modal's ownership generation
    // governs it - being pending is over. Nothing today reads a record left
    // behind (a running open awaits nothing that could be woken, and has already
    // passed the checks above), so this maintains the invariant the set's name
    // asserts rather than fixing a bug - which is exactly why it is worth keeping.
    pendingOpens.delete(pending);
    return new Promise((resolve, reject) => {
        // Per-open ownership generation (DS3-0402): a later open bumps the modal's
        // generation, so this open's token stops matching and all of its callbacks
        // (backdrop, success, cancel, and the async view swap) no-op instead of
        // mutating a singleton a newer open now owns. Claiming also rejects any prior
        // pending open so its caller is never stranded.
        // True once a gesture view has replaced the view that started this login, so
        // its catch handler knows its own buttons are detached and must re-render
        // instead of writing an error nobody can see.
        let gestureViewShown = false;
        // Failure after the second tap: the caller's controls are gone with their
        // view, so put the user back on a live screen carrying the error.
        const failAfterGesture = (message) => {
            swap(renderWallets(message));
        };
        // ONE login at a time, across every lane - and that is TWO different things,
        // which is the distinction this got wrong twice.
        //
        // OWNERSHIP of the card (`activeAttempt`) says whose views and error text may
        // appear. A wallet attempt that is still preparing shares this open's single
        // view and gesture state, so Back, another lane, or a second wallet click
        // must be able to take the card away from it - after which it settles
        // quietly instead of mounting its gesture view over whatever replaced it (or
        // waiting forever on a view ownership will no longer mount). Cancellation is
        // DURABLE because it lives in this token rather than a flag: an attempt
        // cancelled long before it asks for a gesture is simply no longer current, so
        // its gesture rejects the moment it arrives. (A separate "cancelled" boolean
        // could not express that, and the one that existed was set while CLAIMING an
        // attempt, so the first mobile login cancelled its own gesture.)
        //
        // The LOCK (`loginInFlight`) is the other half, and it is not the same thing:
        // taking the card away does not stop the login underneath it. That login can
        // still mint a session and commit itself as the SDK's provider, so starting a
        // second one while the first can still commit is how the app ends up signed
        // in as a wallet the widget never returned. The lock is therefore held until
        // the underlying promise SETTLES, whoever owns the card by then.
        let attemptSeq = 0;
        let activeAttempt = 0;
        let abortGesture = null;
        /** Reject a gesture promise already waiting on a view that is going away. */
        const abortPendingGesture = () => {
            const abort = abortGesture;
            abortGesture = null;
            abort === null || abort === void 0 ? void 0 : abort();
        };
        /** Claim the card, or refuse (null) while a login can still commit. */
        const beginAttempt = () => {
            if (loginInFlight)
                return null;
            loginInFlight = true;
            abortPendingGesture();
            activeAttempt = ++attemptSeq;
            return activeAttempt;
        };
        const attemptIsCurrent = (id) => id === activeAttempt;
        /**
         * Release the lock, and report whether this attempt still owns the card -
         * one call, so a lane cannot release one and forget the other.
         */
        const settleAttempt = (id) => {
            loginInFlight = false;
            if (!attemptIsCurrent(id))
                return false;
            activeAttempt = 0;
            return true;
        };
        /** Take the card back (Back, a lane switch, settling the modal). Deliberately
         *  NOT a release: the login underneath keeps running - see above. */
        const invalidateAttempts = () => {
            activeAttempt = 0;
            abortPendingGesture();
        };
        // Superseded by a newer open: the newer one owns the DOM, so this open can
        // no longer show a gesture view. Cancel any login of ours that is waiting
        // for (or about to ask for) one, then reject our caller.
        const owner = claimModalOpen(m, () => {
            invalidateAttempts();
            reject(new Error("cancelled"));
        });
        // Settle THIS open with a user. Returns false when it no longer owns the card
        // (superseded, dismissed, or already settled).
        // Nothing clears this registration when the open settles, and nothing needs
        // to: settleOwned runs at most once per open, so a stale `liveOpen` is inert
        // - a late success finds a settled owner and quietly does nothing.
        const deliver = (u) => owner.settleOwned(() => {
            invalidateAttempts();
            hideModal(m);
            resolve(u);
        });
        liveOpen = deliver;
        const succeed = (u) => {
            // Recorded FIRST, and unconditionally: an open still preparing has no card
            // to receive this, and reads it when it arrives instead.
            deliverToPendingOpens(u);
            if (deliver(u))
                return;
            // This open is gone, but the login it started COMMITTED: a session is
            // stored and the SDK has an auth provider. Hand it to whatever card is up
            // now instead of leaving the app signed in behind a live login card that
            // will never settle. With no card up there is nothing to settle and the
            // app is simply signed in.
            liveOpen === null || liveOpen === void 0 ? void 0 : liveOpen(u);
        };
        const cancel = () => {
            // Propagated only when the dismissal actually lands: a stale open whose
            // settleOwned no-ops has dismissed nothing, and must not close opens that
            // are preparing for the card a newer open still owns.
            if (!owner.settleOwned(() => {
                // Unwind a login parked on the sign-in gesture before tearing the UI
                // down, so the provider is never left waiting on a tap that can no
                // longer happen.
                invalidateAttempts();
                hideModal(m);
                reject(new Error("cancelled"));
            }))
                return;
            cancelPendingOpens();
        };
        m.onBackdrop = cancel;
        const style = el("style");
        style.textContent = CARD_CSS;
        const renderChoose = () => {
            const card = el("div", "c");
            card.appendChild(style.cloneNode(true));
            const x = el("button", "x", "Close");
            x.type = "button";
            x.setAttribute("aria-label", "Close");
            x.addEventListener("click", cancel);
            card.appendChild(x);
            card.appendChild(el("h1", undefined, title !== null && title !== void 0 ? title : "Sign in"));
            if (subtitle !== "") {
                card.appendChild(el("p", "sub", subtitle !== null && subtitle !== void 0 ? subtitle : "One account and wallet across every Bounded app."));
            }
            const status = el("div", "status");
            const setErr = (msg) => {
                status.textContent = msg;
                status.classList.add("err");
            };
            const setBusy = (btn, label) => {
                status.classList.remove("err");
                status.textContent = "";
                btn.disabled = true;
                btn.replaceChildren(el("span", "spin"), document.createTextNode(label));
            };
            if (hasEmail) {
                const form = el("form");
                const label = el("label");
                label.htmlFor = "bw-email";
                label.textContent = "Email";
                const input = el("input");
                input.id = "bw-email";
                input.type = "email";
                input.autocomplete = "email";
                input.placeholder = "you@example.com";
                input.required = true;
                const submit = el("button", "btn primary");
                submit.type = "submit";
                submit.textContent = "Continue";
                form.append(label, input, submit);
                form.addEventListener("submit", (e) => {
                    e.preventDefault();
                    if (!input.value)
                        return;
                    // Starting another lane takes the card from a wallet attempt still
                    // preparing, and this lane holds the lock until its own request
                    // settles - otherwise a failed email login would keep every other lane
                    // refused for the life of the modal.
                    const attempt = beginAttempt();
                    if (attempt === null) {
                        setErr(BUSY_MSG);
                        return;
                    }
                    if (authMode === "turnkey") {
                        // Turnkey-native: ask Turnkey to email a code, then collect it INLINE.
                        // Sending the code mints nothing, so the lock is released when it
                        // arrives; the code entry claims its own when the user verifies.
                        setBusy(submit, "Sending code\u2026");
                        startTurnkeyEmailLogin(input.value)
                            .then((handle) => { if (settleAttempt(attempt))
                            swap(renderOtp(handle)); })
                            .catch((err) => {
                            if (!settleAttempt(attempt))
                                return;
                            submit.disabled = false;
                            submit.textContent = "Continue";
                            setErr(errText(err));
                        });
                        return;
                    }
                    setBusy(submit, "Opening secure sign-in\u2026");
                    const emailOpts = {
                        provider: "email",
                        loginHint: input.value,
                        redirectUri: opts.redirectUri,
                    };
                    authApi
                        // A hosted login that RESOLVES minted a real session, so it settles
                        // the card even if another lane has since taken it - dropping it
                        // would leave the app signed in behind a widget that says it is not.
                        // Only the failure path belongs to one attempt's UI.
                        .loginWithPopup(emailOpts)
                        .then((u) => { settleAttempt(attempt); succeed(u); })
                        .catch((err) => {
                        // Popup unavailable: same-tab redirect instead. It resolves with
                        // nothing when it is about to navigate, so the only thing that
                        // comes back here is a REJECTION - which must still reach the
                        // handler below, or the lane stays locked with nothing running.
                        if (shouldFallbackToRedirect(err)) {
                            return authApi.loginWithRedirect(emailOpts).then((u) => { if (u)
                                succeed(u); });
                        }
                        throw err;
                    })
                        .catch((err) => {
                        if (!settleAttempt(attempt))
                            return;
                        submit.disabled = false;
                        submit.textContent = "Continue";
                        setErr(errText(err));
                    });
                });
                card.appendChild(form);
            }
            if (social.length || walletEnabled) {
                card.appendChild(el("div", "or", "OR"));
            }
            const labelFor = (method) => { var _a; return (_a = METHOD_LABEL[method]) !== null && _a !== void 0 ? _a : `Continue with ${cap(method)}`; };
            const glyphFor = (method) => method === "google" ? googleGlyph() : null;
            const setLabel = (b, method) => {
                const glyph = glyphFor(method);
                const text = document.createTextNode(labelFor(method));
                if (glyph)
                    b.replaceChildren(glyph, text);
                else
                    b.replaceChildren(text);
            };
            for (const method of social) {
                const b = el("button", "btn ghost");
                b.type = "button";
                setLabel(b, method);
                b.addEventListener("click", () => {
                    const attempt = beginAttempt();
                    if (attempt === null) {
                        setErr(BUSY_MSG);
                        return;
                    }
                    setBusy(b, "Opening secure sign-in\u2026");
                    const socialOpts = { provider: method, redirectUri: opts.redirectUri };
                    authApi
                        .loginWithPopup(socialOpts)
                        .then((u) => { settleAttempt(attempt); succeed(u); })
                        .catch((err) => {
                        if (shouldFallbackToRedirect(err)) {
                            return authApi.loginWithRedirect(socialOpts).then((u) => { if (u)
                                succeed(u); });
                        }
                        throw err;
                    })
                        .catch((err) => {
                        if (!settleAttempt(attempt))
                            return;
                        b.disabled = false;
                        setLabel(b, method);
                        setErr(errText(err));
                    });
                });
                card.appendChild(b);
            }
            if (walletEnabled) {
                const b = el("button", "btn ghost");
                b.type = "button";
                const walletLabel = () => b.replaceChildren(walletGlyph(), document.createTextNode("Continue with wallet"));
                walletLabel();
                b.addEventListener("click", () => {
                    if (wallets.length <= 1) {
                        connectWallet(wallets[0]);
                    }
                    else {
                        swap(renderWallets());
                    }
                });
                card.appendChild(b);
                const connectWallet = (choice) => {
                    const attempt = beginAttempt();
                    if (attempt === null) {
                        setErr(BUSY_MSG);
                        return;
                    }
                    setBusy(b, "Approve in your wallet…");
                    authApi
                        .loginWithWallet(Object.assign(Object.assign({}, (choice ? { getProvider: choice.get } : {})), { confirmSignIn: signInGesture(choice, attempt) }))
                        .then((u) => {
                        // A wallet login that RESOLVED has already minted a session and
                        // committed itself as the SDK's provider - the app IS signed in.
                        // So it settles the card even if the user walked away from this
                        // attempt; leaving the card up would be a lie, and the ownership
                        // generation still stops it from stomping a NEWER open.
                        const owned = settleAttempt(attempt);
                        if (u) {
                            succeed(u);
                            return;
                        }
                        if (!owned)
                            return;
                        b.disabled = false;
                        walletLabel();
                        setErr("Wallet login was cancelled.");
                    })
                        .catch((err) => {
                        if (!settleAttempt(attempt))
                            return;
                        if (gestureViewShown) {
                            gestureViewShown = false;
                            failAfterGesture(errText(err));
                            return;
                        }
                        b.disabled = false;
                        walletLabel();
                        setErr(errText(err));
                    });
                };
            }
            card.appendChild(status);
            const foot = el("div", "foot");
            const poweredBy = el("a", undefined, "Powered by Bounded");
            poweredBy.href = "https://bounded.sh";
            poweredBy.target = "_blank";
            poweredBy.rel = "noopener";
            foot.appendChild(poweredBy);
            card.appendChild(foot);
            return card;
        };
        // Solana Mobile leaves the page to sign: connecting spends the tap that
        // started login, so requesting the signature straight afterwards runs with
        // no transient activation left, Chrome blocks the `solana-wallet:` intent
        // navigation, and the protocol reports the wallet as missing. Collect a
        // second tap on its OWN view - never by adding a listener to a button that
        // already starts a login, which would run both handlers on that tap and
        // kick off a duplicate login beside the one waiting for the gesture.
        // Injected wallets sign in-page and get no extra step.
        // Offered for EVERY wallet: the SDK invokes it only for a wallet that leaves
        // the page (InjectedSolanaProvider.leavesPage), so an in-page wallet still
        // gets no extra tap - and a login that resolves the mobile wallet through
        // the registry fallback is covered too, which a caller-side name check
        // could not do.
        const signInGesture = (choice, attempt) => {
            return () => new Promise((resolveGesture, rejectGesture) => {
                if (!attemptIsCurrent(attempt)) {
                    rejectGesture(new Error("cancelled"));
                    return;
                }
                abortGesture = () => {
                    abortGesture = null;
                    rejectGesture(new Error("cancelled"));
                };
                gestureViewShown = true;
                swap(renderSignInGesture(choice, () => {
                    abortGesture = null;
                    resolveGesture();
                }));
            });
        };
        // The gesture view: one button, one job. Its tap is the fresh activation the
        // wallet's intent navigation needs, so the signature must be requested from
        // this click and nothing else may run on it.
        const renderSignInGesture = (choice, done) => {
            var _a;
            const card = el("div", "c");
            card.appendChild(style.cloneNode(true));
            const x = el("button", "x", "Close");
            x.type = "button";
            x.setAttribute("aria-label", "Close");
            x.addEventListener("click", cancel);
            card.appendChild(x);
            card.appendChild(el("h1", undefined, "One more tap"));
            card.appendChild(el("p", "sub", `${(_a = choice === null || choice === void 0 ? void 0 : choice.name) !== null && _a !== void 0 ? _a : "Your wallet"} is connected. Tap below to sign in - your wallet opens once more to sign.`));
            const b = el("button", "btn primary");
            b.type = "button";
            b.textContent = "Sign in";
            b.addEventListener("click", () => {
                b.disabled = true;
                b.replaceChildren(el("span", "spin"), document.createTextNode("Approve in your wallet…"));
                done();
            }, { once: true });
            card.appendChild(b);
            return card;
        };
        const renderWallets = (initialError) => {
            const card = el("div", "c");
            card.appendChild(style.cloneNode(true));
            const back = el("button", "back", "\u2190 Back");
            back.type = "button";
            // Leaving the wallet list supersedes any attempt still preparing, so it
            // cannot come back later and replace whatever is on screen by then.
            back.addEventListener("click", () => { invalidateAttempts(); swap(renderChoose()); });
            card.appendChild(back);
            card.appendChild(el("h1", undefined, "Connect a wallet"));
            card.appendChild(el("p", "sub", "Choose your Solana wallet."));
            const status = el("div", "status");
            if (initialError) {
                status.textContent = initialError;
                status.classList.add("err");
            }
            // Icon (the wallet's own, from the Wallet Standard registry) + name.
            const setWalletLabel = (b, choice) => {
                const text = document.createTextNode(choice.name);
                if (choice.icon) {
                    const img = el("img", "wicon");
                    img.src = choice.icon;
                    img.alt = "";
                    b.replaceChildren(img, text);
                }
                else {
                    b.replaceChildren(text);
                }
            };
            for (const choice of wallets) {
                const b = el("button", "btn ghost");
                b.type = "button";
                setWalletLabel(b, choice);
                b.addEventListener("click", () => {
                    const attempt = beginAttempt();
                    if (attempt === null) {
                        status.textContent = BUSY_MSG;
                        status.classList.add("err");
                        return;
                    }
                    status.classList.remove("err");
                    b.disabled = true;
                    b.replaceChildren(el("span", "spin"), document.createTextNode("Approve in your wallet\u2026"));
                    authApi
                        .loginWithWallet({ getProvider: choice.get, confirmSignIn: signInGesture(choice, attempt) })
                        .then((u) => {
                        // Settles the card whoever owns it by then - see connectWallet().
                        const owned = settleAttempt(attempt);
                        if (u) {
                            succeed(u);
                            return;
                        }
                        if (!owned)
                            return;
                        b.disabled = false;
                        setWalletLabel(b, choice);
                    })
                        .catch((err) => {
                        if (!settleAttempt(attempt))
                            return;
                        if (gestureViewShown) {
                            gestureViewShown = false;
                            failAfterGesture(errText(err));
                            return;
                        }
                        b.disabled = false;
                        setWalletLabel(b, choice);
                        status.textContent = errText(err);
                        status.classList.add("err");
                    });
                });
                card.appendChild(b);
            }
            card.appendChild(status);
            return card;
        };
        // authMode:"turnkey" inline code entry. Same editorial card; the code is sealed
        // on the Bounded signer origin and verified server-side by Turnkey.
        const renderOtp = (handle) => {
            const card = el("div", "c");
            card.appendChild(style.cloneNode(true));
            const back = el("button", "back", "\u2190 Back");
            back.type = "button";
            // Abandoning the code entry settles the email attempt that opened it, so
            // the next lane the user picks is not refused by a claim nobody owns.
            back.addEventListener("click", () => { invalidateAttempts(); swap(renderChoose()); });
            card.appendChild(back);
            card.appendChild(el("h1", undefined, "Enter your code"));
            card.appendChild(el("p", "sub", `We sent a 6-digit code to ${handle.email}.`));
            const status = el("div", "status");
            const setErr = (msg) => { status.textContent = msg; status.classList.add("err"); };
            const form = el("form");
            const label = el("label");
            label.htmlFor = "bw-otp";
            label.textContent = "Verification code";
            const input = el("input", "otp");
            input.id = "bw-otp";
            input.type = "text";
            input.inputMode = "numeric";
            input.autocomplete = "one-time-code";
            input.maxLength = 6;
            input.placeholder = "000000";
            input.required = true;
            const submit = el("button", "btn primary");
            submit.type = "submit";
            submit.textContent = "Verify";
            form.append(label, input, submit);
            const verify = () => {
                const code = input.value.replace(/\D+/g, "");
                if (code.length < 6) {
                    setErr("Enter the 6-digit code.");
                    return;
                }
                // Verifying MINTS a session, so it takes the lock like every other lane.
                const attempt = beginAttempt();
                if (attempt === null) {
                    setErr(BUSY_MSG);
                    return;
                }
                status.classList.remove("err");
                status.textContent = "";
                submit.disabled = true;
                input.disabled = true;
                submit.replaceChildren(el("span", "spin"), document.createTextNode("Verifying\u2026"));
                handle
                    .verify(code)
                    .then((u) => { settleAttempt(attempt); succeed(u); })
                    .catch((err) => {
                    if (!settleAttempt(attempt))
                        return;
                    submit.disabled = false;
                    input.disabled = false;
                    submit.textContent = "Verify";
                    input.value = "";
                    input.focus();
                    setErr(errText(err));
                });
            };
            form.addEventListener("submit", (e) => { e.preventDefault(); verify(); });
            // Auto-submit once six digits are in - the buttery one-shot paste/type path.
            input.addEventListener("input", () => {
                if (input.value.replace(/\D+/g, "").length === 6 && !submit.disabled)
                    verify();
            });
            card.appendChild(form);
            card.appendChild(status);
            const foot = el("div", "foot");
            const poweredBy = el("a", undefined, "Powered by Bounded");
            poweredBy.href = "https://bounded.sh";
            poweredBy.target = "_blank";
            poweredBy.rel = "noopener";
            foot.appendChild(poweredBy);
            card.appendChild(foot);
            requestAnimationFrame(() => input.focus());
            return card;
        };
        const swap = (node) => {
            // A superseded open must not remount or re-fit the shared singleton modal -
            // an async .then (e.g. the turnkey email lane calling swap(renderOtp)) can
            // land after a newer open has taken over the DOM.
            if (!owner.owns())
                return;
            mountContent(m, node);
            requestAnimationFrame(() => {
                if (owner.owns())
                    fitPanelToContent(m);
            });
        };
        swap(renderChoose());
        showModal(m);
    });
}
function cap(s) {
    return s ? s[0].toUpperCase() + s.slice(1) : s;
}
function errText(err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (/blocked/i.test(msg))
        return "Popup blocked - allow popups and try again.";
    return msg || "Something went wrong. Try again.";
}
function shouldFallbackToRedirect(err) {
    const msg = err instanceof Error ? err.message : String(err);
    return /blocked/i.test(msg) || /use loginWithRedirect\(\) instead/i.test(msg);
}
